import type { ScaleTime } from "d3-scale";
import { forwardRef, useId, useMemo } from "react";
import { getTickTimeFormat } from "../../../utils/timeFormat";
import { type LiveTimePreviewHandle, useLiveTimeTicks } from "./useLiveTimeTicks";

/**
 * Bottom time axis for the camera chart. Calendar-aligned ("nice") tick labels
 * re-generated against the live domain every frame (via the shared
 * useLiveTimeTicks hook), so they slide and enter/exit at the edges in lockstep
 * with the data and snap to nothing on release. Only this small subtree
 * re-renders per frame — the data series ride the group's GPU transform.
 */
export type { LiveTimePreviewHandle } from "./useLiveTimeTicks";

interface LiveTimeAxisBottomProps {
  top: number;
  scale: ScaleTime<number, number>;
  width: number;
}

const TICK_LENGTH = 6;
const LABEL_BASELINE = 20;

export const LiveTimeAxisBottom = forwardRef<LiveTimePreviewHandle, LiveTimeAxisBottomProps>(
  ({ top, scale, width }, ref) => {
    const { activeScale, ticks, start, end, valid, compact, rangeStart, rangeEnd } = useLiveTimeTicks(
      scale,
      width,
      ref
    );
    const stepMs = ticks.length >= 2 ? ticks[1].getTime() - ticks[0].getTime() : end.getTime() - start.getTime();
    const fmt = useMemo(() => (valid ? getTickTimeFormat(stepMs, { compact }) : () => ""), [stepMs, compact, valid]);

    const clipId = useId();
    const clipLeft = Math.min(rangeStart, rangeEnd);
    const clipWidth = Math.abs(rangeEnd - rangeStart);

    return (
      <g>
        <line x1={rangeStart} x2={rangeEnd} y1={top} y2={top} stroke="var(--color-border-muted)" strokeWidth={1} />
        {ticks.length > 0 && (
          <>
            <defs>
              <clipPath id={clipId}>
                <rect x={clipLeft} y={top - 4} width={clipWidth} height={LABEL_BASELINE + 16} />
              </clipPath>
            </defs>
            <g clipPath={`url(#${clipId})`}>
              {ticks.map((d) => (
                <g key={d.getTime()} transform={`translate(${activeScale(d)},${top})`}>
                  <line x1={0} x2={0} y1={0} y2={TICK_LENGTH} stroke="var(--color-border-muted)" strokeWidth={1} />
                  <text x={0} y={LABEL_BASELINE} textAnchor="middle" fontSize={12} fill="var(--color-text-secondary)">
                    {fmt(d)}
                  </text>
                </g>
              ))}
            </g>
          </>
        )}
      </g>
    );
  }
);

LiveTimeAxisBottom.displayName = "LiveTimeAxisBottom";
